import * as Sentry from "@sentry/react";
import { FormLabel, FormControl, RadioGroup, FormControlLabel, FormHelperText, Radio } from "@mui/material";
import { RadioGroupProps } from "@mui/material/RadioGroup";
import React from "react";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";

export type FormRadioGroupValue = null | string | boolean | number;

export interface IOption {
  label: string;
  value: FormRadioGroupValue;
}

export type FormRadioGroupProps<TFormValues extends FieldValues> = Omit<RadioGroupProps, "name" | "defaultValue"> & {
  name: Path<TFormValues>;
  options?: IOption[];
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  disabled?: boolean;
};

export function FormRadioGroup<TFormValues extends FieldValues>({
  name,
  options = [],
  label = "",
  helperText = "",
  row = true,
  disabled = false,
  ...props
}: FormRadioGroupProps<TFormValues>) {
  const { control, setValue } = useFormContext<TFormValues>();

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error, isTouched } }) => (
          <FormControl error={!!(error && isTouched)} disabled={disabled}>
            {label && <FormLabel>{label}</FormLabel>}

            <RadioGroup
              name={name}
              value={field.value}
              onChange={(event) => {
                const value = event.target.value;
                const transformedValue = value === "true" ? true : value === "false" ? false : value;
                setValue(name, transformedValue as PathValue<TFormValues, Path<TFormValues>>, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              row={row}
              {...props}
            >
              {options.map(({ label, value }) => {
                return <FormControlLabel key={label} label={label} value={value} control={<Radio size="small" />} />;
              })}
            </RadioGroup>

            {error && isTouched ? (
              <FormHelperText>{error.message}</FormHelperText>
            ) : (
              helperText && <FormHelperText>{helperText}</FormHelperText>
            )}
          </FormControl>
        )}
      />
    </Sentry.ErrorBoundary>
  );
}
